import { keyBy } from 'es-toolkit/compat';
import { validateRequiredFieldValue } from '../fields';
import type {
  Field,
  FormAction,
  FormChangedFieldAction,
  FormInitAction,
  FormSetErrorAction,
  FormSetStartedAction,
  FormSetStatusAction,
  FormState,
  WidgetAction,
  WidgetState,
  WidgetToggleCloseAction,
  WidgetToggleSubmittedAction
} from './types';

export function createFormInitialState() {
  return {
    fields: {},
    fieldsError: {
      required: false,
      invalid: false
    },
    started: {
      value: false
    },
    status: undefined
  } satisfies FormState;
}

export const widgetInitialState = {
  closed: false,
  submitted: false
};

export function formReducer(state: FormState, action: FormAction) {
  return formActions[action.type](state, action);
}

export function widgetReducer(state: WidgetState, action: WidgetAction) {
  return widgetActions[action.type](state, action);
}

const formActions = {
  reset: () => createFormInitialState(),
  init: (state: FormState, action: FormInitAction) => {
    const { fields: derivedFields } = action;

    const fields = derivedFields.map(
      ({ type, id, required, text, label, placeholder, fieldID }) => ({
        ...state.fields[id],
        type,
        id,
        required,
        value: state.fields[id]?.value,
        name: label?.trim() || placeholder || text || id,
        fieldID:
          fieldID ||
          (label?.trim() || placeholder || id)
            ?.replace(/\s+/g, '-')
            .toLowerCase()
      })
    );

    const newFields = keyBy(fields, 'id');
    const newFieldsError = {
      ...state.fieldsError,
      required: fields.some(validateRequiredFieldValue)
    };

    return {
      ...state,
      fields: newFields,
      fieldsError: newFieldsError
    };
  },
  changeField: (state: FormState, action: FormChangedFieldAction) => {
    const { id } = action.field;

    const updatedFields = {
      ...state.fields,
      [id]: {
        ...state.fields[id],
        ...action.field
      }
    };

    const errors = formFieldsValidation(updatedFields);
    const [invalid, required] = errors;

    const updatedFieldsError = {
      ...state.fieldsError,
      invalid,
      required
    };

    const nextState: FormState = {
      ...state,
      fields: updatedFields,
      fieldsError: updatedFieldsError
    };

    if (state.status === 'invalid' && errors.filter(Boolean).length === 0) {
      nextState.status = undefined;
    }

    return nextState;
  },
  setError: (state: FormState, action: FormSetErrorAction) => {
    const { fieldsError, status } = action;

    return {
      ...state,
      fieldsError: {
        ...state.fieldsError,
        ...fieldsError
      },
      status
    };
  },
  setStatus: (state: FormState, action: FormSetStatusAction) => {
    const { status } = action;

    return {
      ...state,
      status
    };
  },

  setStarted: (state: FormState, action: FormSetStartedAction) => {
    const { started } = action;

    return {
      ...state,
      started: {
        ...state.started,
        value: started
      }
    };
  }
} as {
  [index: string]: (state?: FormState, action?: FormAction) => FormState;
};

const widgetActions = {
  resetWidget: () => widgetInitialState,
  toggleClose: (state: WidgetState, action: WidgetToggleCloseAction) => {
    return { ...state, closed: action.closed };
  },
  toggleSubmitted: (
    state: WidgetState,
    action: WidgetToggleSubmittedAction
  ) => {
    return { ...state, submitted: action.submitted };
  }
} as {
  [index: string]: (state?: WidgetState, action?: WidgetAction) => WidgetState;
};

const formFieldsValidation = (fields: { [index: string]: Field }) => {
  const validation = Object.values(fields).map(({ valid, ...field }) => {
    return [valid === false, validateRequiredFieldValue(field)];
  });

  const hasInvalid = validation.some(([invalid]) => invalid);
  const hasRequired = validation.some(([, required]) => required);

  return [hasInvalid, hasRequired];
};
